/**
 * Lightweight user-agent parsing into browser + OS display labels.
 * Moved here from the Ryn Replay player (#30211) so the help desk ticket context
 * (US 34308) can share it without a web-lib cycle. Imported through its own
 * light entry, `@ups/xplat/utils/user-agent`, so pure specs never load the Angular
 * parts of the `@ups/xplat/utils` barrel.
 *
 * Display-only. Keep heuristics conservative, prefer "Unknown" over a
 * confident wrong label.
 */

export type UserAgentBrowserFamily =
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'safari'
  | 'other';

export interface IParsedUserAgent {
  browserFamily: UserAgentBrowserFamily;
  browserLabel: string;
  osLabel: string;
}

const UNKNOWN = 'Unknown';

/**
 * Parse a stored user-agent string into browser + OS labels for display.
 */
export const parseUserAgent = (
  userAgent: string | null | undefined
): IParsedUserAgent | undefined => {
  const ua = userAgent?.trim();
  if (!ua) {
    return undefined;
  }

  const browserFamily = detectBrowserFamily(ua);
  const browserVersion = detectBrowserVersion(ua, browserFamily);
  const browserName = browserFamilyLabel(browserFamily);
  const browserLabel = browserVersion
    ? `${browserName} ${browserVersion}`
    : browserName;

  return {
    browserFamily,
    browserLabel,
    osLabel: detectOsLabel(ua),
  };
};

export const detectBrowserFamily = (ua: string): UserAgentBrowserFamily => {
  if (/Edg\//i.test(ua)) {
    return 'edge';
  }
  if (/Firefox\//i.test(ua)) {
    return 'firefox';
  }
  if (/Chrome\//i.test(ua) || /Chromium\//i.test(ua)) {
    return 'chrome';
  }
  if (/Safari\//i.test(ua)) {
    return 'safari';
  }
  return 'other';
};

const browserFamilyLabel = (family: UserAgentBrowserFamily): string => {
  switch (family) {
    case 'chrome':
      return 'Chrome';
    case 'edge':
      return 'Edge';
    case 'firefox':
      return 'Firefox';
    case 'safari':
      return 'Safari';
    default:
      return UNKNOWN;
  }
};

const detectBrowserVersion = (
  ua: string,
  family: UserAgentBrowserFamily
): string | undefined => {
  let match: RegExpMatchArray | null = null;
  switch (family) {
    case 'edge':
      match = ua.match(/Edg\/([\d.]+)/i);
      break;
    case 'firefox':
      match = ua.match(/Firefox\/([\d.]+)/i);
      break;
    case 'chrome':
      match = ua.match(/(?:Chrome|Chromium)\/([\d.]+)/i);
      break;
    case 'safari':
      match = ua.match(/Version\/([\d.]+)/i) ?? ua.match(/Safari\/([\d.]+)/i);
      break;
    default:
      return undefined;
  }
  const raw = match?.[1];
  if (!raw) {
    return undefined;
  }
  // Major.minor is enough for a chip; full builds are noise.
  const parts = raw.split('.');
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0];
};

const detectOsLabel = (ua: string): string => {
  if (/Windows NT 10/i.test(ua)) {
    return 'Windows 10/11';
  }
  if (/Windows NT/i.test(ua)) {
    return 'Windows';
  }
  if (/Mac OS X ([\d_]+)/i.test(ua)) {
    const ver = ua.match(/Mac OS X ([\d_]+)/i)?.[1]?.replace(/_/g, '.');
    return ver ? `macOS ${ver}` : 'macOS';
  }
  if (/Android ([\d.]+)/i.test(ua)) {
    const ver = ua.match(/Android ([\d.]+)/i)?.[1];
    return ver ? `Android ${ver}` : 'Android';
  }
  if (/iPhone|iPad|iPod/i.test(ua)) {
    const ver = ua.match(/OS ([\d_]+)/i)?.[1]?.replace(/_/g, '.');
    return ver ? `iOS ${ver}` : 'iOS';
  }
  if (/Linux/i.test(ua)) {
    return 'Linux';
  }
  return UNKNOWN;
};
